import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { projects, type Project, type ProjectKind, type ProjectStatus } from '@/api/projects';
import { useConfirm } from '@/components/ConfirmProvider';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';
import DatePopover from '@/components/DatePopover';

const KIND_LABELS: Record<ProjectKind, string> = {
  DIRECTION: 'Напрям',
  PROJECT: 'Проект',
};

const STATUS_LABELS: Record<ProjectStatus, string> = {
  PLANNED: '🕓 Запланований',
  ACTIVE: '✓ Активний',
  PAUSED: '⏸ Призупинений',
  COMPLETED: '✓ Завершений',
  CANCELLED: '✗ Скасований',
};

interface Editing {
  id?: string;
  code: string;
  name: string;
  full_name: string;
  kind: ProjectKind;
  status: ProjectStatus;
  start_date: string;
  end_date: string;
  description: string;
}

const EMPTY: Editing = {
  code: '', name: '', full_name: '',
  kind: 'PROJECT', status: 'ACTIVE',
  start_date: '', end_date: '', description: '',
};

export default function ProjectsPage() {
  const confirm = useConfirm();
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ['projects'],
    queryFn: () => projects.list({ limit: 500 }),
  });
  const [editing, setEditing] = useState<Editing | null>(null);
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => editing!.id
      ? projects.update(editing!.id, {
          code: editing!.code, name: editing!.name,
          full_name: editing!.full_name || null,
          kind: editing!.kind, status: editing!.status,
          start_date: editing!.start_date || null,
          end_date: editing!.end_date || null,
          description: editing!.description || null,
        })
      : projects.create({
          code: editing!.code, name: editing!.name,
          full_name: editing!.full_name || null,
          kind: editing!.kind, status: editing!.status,
          start_date: editing!.start_date || null,
          end_date: editing!.end_date || null,
          description: editing!.description || null,
        }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['projects'] });
      setEditing(null);
    },
    onError: (e) => setError(extractError(e)),
  });
  const remove = useMutation({
    mutationFn: (id: string) => projects.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['projects'] }),
  });

  const cols = useMemo<ColumnDef<Project>[]>(() => [
    {
      key: 'code', header: 'Код', width: 130, importBadge: true,
      sortValue: (p) => p.code ?? '',
      cell: (p) => <span style={{ fontFamily: 'monospace' }}>{p.code}</span>,
    },
    {
      // Без width — гнучка колонка: займає вільний простір.
      key: 'name', header: 'Назва',
      sortValue: (p) => (p.name ?? '').toLowerCase(), cell: (p) => p.name,
    },
    {
      key: 'kind', header: 'Тип', width: 140,
      sortValue: (p) => KIND_LABELS[p.kind] ?? p.kind,
      cell: (p) => KIND_LABELS[p.kind] ?? p.kind,
    },
    {
      key: 'status', header: 'Статус', width: 160,
      sortValue: (p) => STATUS_LABELS[p.status] ?? p.status,
      cell: (p) => STATUS_LABELS[p.status] ?? p.status,
    },
    {
      key: 'period', header: 'Період', width: 130,
      cell: (p) => (
        <span style={{ fontSize: '0.8rem' }}>
          {p.start_date ? new Date(p.start_date).toLocaleDateString('uk-UA') : '—'}
          {' / '}
          {p.end_date ? new Date(p.end_date).toLocaleDateString('uk-UA') : '∞'}
        </span>
      ),
    },
    {
      key: 'actions', header: '', width: 110, align: 'right',
      cell: (p) => (
        <>
          <button onClick={() => setEditing({
            id: p.id, code: p.code, name: p.name,
            full_name: p.full_name ?? '',
            kind: p.kind, status: p.status,
            start_date: p.start_date ?? '',
            end_date: p.end_date ?? '',
            description: p.description ?? '',
          })}>✎</button>
          <button className="danger" style={{ marginLeft: '0.3rem' }}
                  onClick={() => confirm('Видалити проект?').then((ok) => { if (ok) remove.mutate(p.id); })}>
            ×
          </button>
        </>
      ),
    },
  ], [confirm, remove]);

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Проекти / напрями</h2>
        <button className="primary" onClick={() => setEditing({ ...EMPTY })}>
          + Новий проект
        </button>
      </div>
      <p style={{ color: 'var(--muted)', fontSize: '0.85rem' }}>
        Проекти / напрями використовуються для управлінського обліку — щоб
        бачити прибутковість окремо по напрямах діяльності, кампаніях,
        великих контрактах. Прив'язуйте до проекту документи (продажу,
        закупки, ЗП, банк) — і потім дивіться P&L по проектах.
      </p>

      {editing && (
        <div style={{ background: 'var(--bg-soft-2)', padding: '1rem',
                      borderRadius: 'var(--radius)', marginBottom: '1rem',
                      border: '1px solid var(--border)' }}>
          <div className="doc-grid">
            <div className="field" style={{ gridColumn: 'span 2' }}>
              <label>Код</label>
              <input value={editing.code}
                     onChange={(e) => setEditing({ ...editing, code: e.target.value })} />
            </div>
            <div className="field" style={{ gridColumn: 'span 4' }}>
              <label>Назва</label>
              <input value={editing.name}
                     onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
            </div>
            <div className="field" style={{ gridColumn: 'span 3' }}>
              <label>Тип</label>
              <select value={editing.kind}
                      onChange={(e) => setEditing({ ...editing, kind: e.target.value as ProjectKind })}>
                {(Object.keys(KIND_LABELS) as ProjectKind[]).map((k) =>
                  <option key={k} value={k}>{KIND_LABELS[k]}</option>)}
              </select>
            </div>
            <div className="field" style={{ gridColumn: 'span 3' }}>
              <label>Статус</label>
              <select value={editing.status}
                      onChange={(e) => setEditing({ ...editing, status: e.target.value as ProjectStatus })}>
                {(Object.keys(STATUS_LABELS) as ProjectStatus[]).map((k) =>
                  <option key={k} value={k}>{STATUS_LABELS[k]}</option>)}
              </select>
            </div>
            <div className="field" style={{ gridColumn: 'span 2' }}>
              <label>Повна назва</label>
              <input value={editing.full_name}
                     onChange={(e) => setEditing({ ...editing, full_name: e.target.value })} />
            </div>
            <div className="field" style={{ gridColumn: 'span 4' }}>
              <label>Дата початку</label>
              <DatePopover value={editing.start_date}
                     onChange={(v) => setEditing({ ...editing, start_date: v })} />
            </div>
            <div className="field" style={{ gridColumn: 'span 3' }}>
              <label>Дата завершення</label>
              <DatePopover value={editing.end_date}
                     onChange={(v) => setEditing({ ...editing, end_date: v })} />
            </div>
            <div className="field" style={{ gridColumn: 'span 3' }}>
              <label>Опис</label>
              <input value={editing.description}
                     onChange={(e) => setEditing({ ...editing, description: e.target.value })} />
            </div>
          </div>
          <div style={{ marginTop: '0.5rem', display: 'flex', gap: '0.5rem' }}>
            <button className="primary" onClick={() => save.mutate()}>Зберегти</button>
            <button onClick={() => { setEditing(null); setError(null); }}>Скасувати</button>
          </div>
          {error && <div className="error">{error}</div>}
        </div>
      )}

      <DataTable<Project>
        rows={list.data?.items ?? []}
        columns={cols}
        rowKey={(p) => p.id}
        loading={list.isLoading}
        emptyText="Немає проектів."
        rowMuted={(p) => p.deletion_mark ? 0.4 : undefined}
        initialSortKey="code"
      />
    </div>
  );
}

function extractError(e: unknown): string {
  if (typeof e === 'object' && e && 'response' in e) {
    const r = (e as { response?: { data?: { detail?: string } } }).response;
    return r?.data?.detail ?? 'Error';
  }
  return String(e);
}
